// ** React Imports
import { useState } from "react";

// ** Third Party Components
import Cookies from "js-cookie";

// ** Config
import configGlobal from "configuration";

/**
 * Custom hook to manage Firebase token/state with migration from localStorage to cookies.
 *
 * @param defaultValue - initial value if there is nothing in cookies or localStorage.
 * @returns {object} – { firebaseCookieState, setFirebaseCookieState, removeFirebaseCookieState }
 */
export const useFirebaseCookieState = ({ defaultValue = null } = {}) => {
	const { cookieName = null, cookieOptionsJsCookie = {} } =
		configGlobal?.firebaseData ?? {};
	const localStorageKey = configGlobal?.firebase?.localStorage?.itemName;

	const [value, setValueState] = useState(() => {
		if (!cookieName) return defaultValue;

		let initialValue = defaultValue;

		try {
			// ** Try to get from localStorage (old location)
			if (
				typeof window !== "undefined" &&
				window.localStorage &&
				localStorageKey
			) {
				const oldLS = localStorage.getItem(localStorageKey);
				if (oldLS !== null) {
					initialValue = JSON.parse(oldLS);
					// ** Remove old key from localStorage after migration.
					// localStorage.removeItem(localStorageKey);
				}
			}

			// ** If cookie exists, overwrite with cookie value.
			const cookie = Cookies.get(cookieName);
			if (cookie !== undefined) {
				initialValue = JSON.parse(cookie);
			}

			// ** Return final value.
			return initialValue;
		} catch (e) {
			// ** If something went wrong, remove cookie and fallback.
			Cookies.remove(cookieName);
			return defaultValue;
		}
	});

	const setFirebaseCookieState = (newValue) => {
		const finalValue =
			newValue instanceof Function ? newValue(value) : newValue;

		// ** Update React state.
		setValueState(finalValue);

		if (!cookieName) return;

		// ** Save to cookie.
		if (finalValue === null || finalValue === undefined) {
			try {
				Cookies.remove(cookieName, cookieOptionsJsCookie);
			} catch (e) {
				// ** Ignore removal failures.
			}
		} else {
			try {
				Cookies.set(
					cookieName,
					JSON.stringify(finalValue),
					cookieOptionsJsCookie,
				);
			} catch (e) {
				// ** Ignore write failures.
			}
		}
	};

	const removeFirebaseCookieState = () => {
		setValueState(null);
		if (cookieName) {
			try {
				Cookies.remove(cookieName, cookieOptionsJsCookie);
			} catch (e) {
				// ** Ignore removal failures.
			}
		}
	};

	return {
		firebaseCookieState: value,
		setFirebaseCookieState,
		removeFirebaseCookieState,
	};
};
